var __values = (this && this.__values) || function (o) {
    var m = typeof Symbol === "function" && o[Symbol.iterator], i = 0;
    if (m) return m.call(o);
    return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { EJSON } from "bson";
import { wrapDecodingError } from "../../internal/common/StitchErrorUtils";
import Headers from "../../internal/net/Headers";
import Method from "../../internal/net/Method";
import { StitchAuthDocRequest } from "../../internal/net/StitchAuthDocRequest";
import { StitchAuthRequest } from "../../internal/net/StitchAuthRequest";
import { StitchDocRequest } from "../../internal/net/StitchDocRequest";
import StitchClientError from "../../StitchClientError";
import { StitchClientErrorCode } from "../../StitchClientErrorCode";
import StitchError from "../../StitchError";
import StitchRequestError from "../../StitchRequestError";
import { StitchRequestErrorCode } from "../../StitchRequestErrorCode";
import StitchServiceError from "../../StitchServiceError";
import { StitchServiceErrorCode } from "../../StitchServiceErrorCode";
import Stream from "../../Stream";
import CoreStitchUserImpl from "../internal/CoreStitchUserImpl";
import AnonymousAuthProvider from "../providers/anonymous/AnonymousAuthProvider";
import StitchAuthResponseCredential from "../providers/internal/StitchAuthResponseCredential";
import AccessTokenRefresher from "./AccessTokenRefresher";
import { AuthEventKind } from "./AuthEvent";
import AuthInfo from "./AuthInfo";
import JWT from "./JWT";
import ApiAuthInfo from "./models/ApiAuthInfo";
import ApiCoreUserProfile from "./models/ApiCoreUserProfile";
import { readActiveUserFromStorage, readCurrentUsersFromStorage, writeActiveUserAuthInfoToStorage, writeAllUsersAuthInfoToStorage } from "./models/StoreAuthInfo";
var OPTIONS = "options";
var DEVICE = "device";
var CoreStitchAuth = (function () {
    function CoreStitchAuth(requestClient, authRoutes, storage, useTokenRefresher) {
        if (useTokenRefresher === void 0) { useTokenRefresher = true; }
        this.requestClient = requestClient;
        this.authRoutes = authRoutes;
        this.storage = storage;
        var allUsersAuthInfo;
        try {
            allUsersAuthInfo = readCurrentUsersFromStorage(storage);
        }
        catch (e) {
            throw new StitchClientError(StitchClientErrorCode.CouldNotLoadPersistedAuthInfo);
        }
        this.allUsersAuthInfo = allUsersAuthInfo;
        var activeUserAuthInfo;
        try {
            activeUserAuthInfo = readActiveUserFromStorage(storage);
        }
        catch (e) {
            throw new StitchClientError(StitchClientErrorCode.CouldNotLoadPersistedAuthInfo);
        }
        this.activeUserAuthInfo =
            activeUserAuthInfo === undefined ? AuthInfo.empty() : activeUserAuthInfo;
        if (this.activeUserAuthInfo.hasUser) {
            this.currentUser = this.prepUser(this.activeUserAuthInfo);
        }
        if (useTokenRefresher) {
            this.accessTokenRefresher = new AccessTokenRefresher(this);
            this.accessTokenRefresher.run();
        }
    }
    Object.defineProperty(CoreStitchAuth.prototype, "authInfo", {
        get: function () {
            return this.activeUserAuthInfo;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CoreStitchAuth.prototype, "isLoggedIn", {
        get: function () {
            return this.currentUser !== undefined && this.currentUser.isLoggedIn;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CoreStitchAuth.prototype, "user", {
        get: function () {
            return this.currentUser;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CoreStitchAuth.prototype, "hasDeviceId", {
        get: function () {
            return (this.activeUserAuthInfo.deviceId !== undefined &&
                this.activeUserAuthInfo.deviceId !== "" &&
                this.activeUserAuthInfo.deviceId !== "000000000000000000000000");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CoreStitchAuth.prototype, "deviceId", {
        get: function () {
            if (!this.hasDeviceId) {
                return undefined;
            }
            return this.activeUserAuthInfo.deviceId;
        },
        enumerable: true,
        configurable: true
    });
    CoreStitchAuth.prototype.listUsers = function () {
        var _this = this;
        var list = [];
        this.allUsersAuthInfo.forEach(function (authInfo) {
            list.push(_this.prepUser(authInfo));
        });
        return list;
    };
    CoreStitchAuth.prototype.doAuthenticatedRequest = function (stitchReq, authInfo) {
        var _this = this;
        try {
            return this.requestClient
                .doRequest(this.prepareAuthRequest(stitchReq, authInfo || this.activeUserAuthInfo))
                .catch(function (err) {
                return _this.handleAuthFailure(err, stitchReq);
            });
        }
        catch (err) {
            return Promise.reject(err);
        }
    };
    CoreStitchAuth.prototype.doAuthenticatedRequestWithDecoder = function (stitchReq, decoder) {
        return this.doAuthenticatedRequest(stitchReq)
            .then(function (response) {
            var obj = EJSON.parse(response.body, { strict: false });
            if (decoder) {
                return decoder.decode(obj);
            }
            return obj;
        })
            .catch(function (err) {
            throw wrapDecodingError(err);
        });
    };
    CoreStitchAuth.prototype.openAuthenticatedEventStream = function (stitchReq, open) {
        var _this = this;
        if (open === void 0) { open = true; }
        if (!this.isLoggedIn) {
            throw new StitchClientError(StitchClientErrorCode.MustAuthenticateFirst);
        }
        var authToken = stitchReq.useRefreshToken ?
            this.activeUserAuthInfo.refreshToken : this.activeUserAuthInfo.accessToken;
        return this.requestClient.doStreamRequest(stitchReq.builder
            .withPath(stitchReq.path + "&stitch_at=" + authToken)
            .build(), open, function () { return _this.openAuthenticatedEventStream(stitchReq, false); })
            .catch(function (err) {
            return _this.handleAuthFailureForEventStream(err, stitchReq, open);
        });
    };
    CoreStitchAuth.prototype.openAuthenticatedStreamWithDecoder = function (stitchReq, decoder) {
        return this.openAuthenticatedEventStream(stitchReq)
            .then(function (eventStream) {
            return new Stream(eventStream, decoder);
        });
    };
    CoreStitchAuth.prototype.refreshAccessToken = function () {
        var _this = this;
        var reqBuilder = new StitchAuthRequest.Builder()
            .withRefreshToken()
            .withPath(this.authRoutes.sessionRoute)
            .withMethod(Method.POST);
        return this.doAuthenticatedRequest(reqBuilder.build()).then(function (response) {
            try {
                var partialInfo = ApiAuthInfo.fromJSON(JSON.parse(response.body));
                _this.activeUserAuthInfo = _this.activeUserAuthInfo.merge(partialInfo);
                if (partialInfo.accessToken && _this.user instanceof CoreStitchUserImpl) {
                    var userData = JWT.fromEncoded(partialInfo.accessToken).userData;
                    _this.user.customData = userData === undefined ? {} : userData;
                }
            }
            catch (err) {
                throw new StitchRequestError(err, StitchRequestErrorCode.DECODING_ERROR);
            }
            try {
                writeActiveUserAuthInfoToStorage(_this.activeUserAuthInfo, _this.storage);
                _this.allUsersAuthInfo.set(_this.activeUserAuthInfo.userId, _this.activeUserAuthInfo);
                writeAllUsersAuthInfoToStorage(_this.allUsersAuthInfo, _this.storage);
            }
            catch (err) {
                throw new StitchClientError(StitchClientErrorCode.CouldNotPersistAuthInfo);
            }
        });
    };
    CoreStitchAuth.prototype.switchToUserWithId = function (userId) {
        var authInfo = this.allUsersAuthInfo.get(userId);
        if (authInfo === undefined) {
            throw new StitchClientError(StitchClientErrorCode.UserNotFound);
        }
        if (!authInfo.isLoggedIn) {
            throw new StitchClientError(StitchClientErrorCode.UserNotLoggedIn);
        }
        if (this.activeUserAuthInfo.hasUser) {
            this.allUsersAuthInfo.set(this.activeUserAuthInfo.userId, this.activeUserAuthInfo.withNewAuthActivityTime());
        }
        var newAuthInfo = authInfo.withNewAuthActivityTime();
        this.allUsersAuthInfo.set(userId, newAuthInfo);
        writeActiveUserAuthInfoToStorage(newAuthInfo, this.storage);
        this.activeUserAuthInfo = newAuthInfo;
        var previousUser = this.currentUser;
        this.currentUser = this.prepUser(newAuthInfo);
        this.onAuthEvent();
        this.dispatchAuthEvent({
            currentActiveUser: this.currentUser,
            kind: AuthEventKind.ActiveUserChanged,
            previousActiveUser: previousUser
        });
        return this.currentUser;
    };
    CoreStitchAuth.prototype.loginWithCredentialInternal = function (credential) {
        var _this = this;
        var e_1, _a;
        if (credential instanceof StitchAuthResponseCredential) {
            return this.processLogin(credential, credential.authInfo, credential.asLink).then(function (user) {
                _this.dispatchAuthEvent({
                    kind: AuthEventKind.UserLoggedIn,
                    loggedInUser: user
                });
                return user;
            });
        }
        if (credential.providerCapabilities.reusesExistingSession) {
            try {
                for (var _b = __values(this.allUsersAuthInfo), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var _d = __read(_c.value, 2), userId = _d[0], authInfo = _d[1];
                    if (authInfo.loggedInProviderType === credential.providerType) {
                        if (authInfo.isLoggedIn) {
                            try {
                                return Promise.resolve(this.switchToUserWithId(userId));
                            }
                            catch (error) {
                                return Promise.reject(error);
                            }
                        }
                        if (authInfo.userId !== undefined) {
                            this.removeUserWithIdInternal(authInfo.userId);
                        }
                    }
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_1) throw e_1.error; }
            }
        }
        return this.doLogin(credential, false);
    };
    CoreStitchAuth.prototype.linkUserWithCredentialInternal = function (user, credential) {
        if (this.currentUser !== undefined && user.id !== this.currentUser.id) {
            return Promise.reject(new StitchClientError(StitchClientErrorCode.UserNoLongerValid));
        }
        return this.doLogin(credential, true);
    };
    CoreStitchAuth.prototype.logoutInternal = function () {
        if (!this.isLoggedIn || !this.currentUser) {
            return Promise.resolve();
        }
        return this.logoutUserWithIdInternal(this.currentUser.id);
    };
    CoreStitchAuth.prototype.logoutUserWithIdInternal = function (userId) {
        var _this = this;
        var authInfo = this.allUsersAuthInfo.get(userId);
        if (authInfo === undefined) {
            return Promise.reject(new StitchClientError(StitchClientErrorCode.UserNotFound));
        }
        if (!authInfo.isLoggedIn) {
            return Promise.resolve();
        }
        var clearAuthBlock = function () {
            _this.clearUserAuthTokens(authInfo.userId);
            if (authInfo.loggedInProviderType === AnonymousAuthProvider.TYPE) {
                _this.removeUserWithIdInternal(authInfo.userId);
            }
        };
        return this.doLogout(authInfo)
            .then(function () {
            clearAuthBlock();
        })
            .catch(function () {
            clearAuthBlock();
        });
    };
    CoreStitchAuth.prototype.removeUserInternal = function () {
        if (!this.isLoggedIn || this.currentUser === undefined) {
            return Promise.resolve();
        }
        return this.removeUserWithIdInternal(this.currentUser.id);
    };
    CoreStitchAuth.prototype.removeUserWithIdInternal = function (userId) {
        var _this = this;
        var authInfo = this.allUsersAuthInfo.get(userId);
        if (authInfo === undefined) {
            return Promise.reject(new StitchClientError(StitchClientErrorCode.UserNotFound));
        }
        var removeBlock = function () {
            _this.clearUserAuthTokens(authInfo.userId);
            _this.allUsersAuthInfo.delete(userId);
            writeAllUsersAuthInfoToStorage(_this.allUsersAuthInfo, _this.storage);
            var removedUser = _this.prepUser(authInfo.loggedOut());
            _this.onAuthEvent();
            _this.dispatchAuthEvent({
                kind: AuthEventKind.UserRemoved,
                removedUser: removedUser
            });
        };
        if (authInfo.isLoggedIn) {
            return this.doLogout(authInfo).then(function () {
                removeBlock();
            }).catch(function (err) {
                removeBlock();
            });
        }
        removeBlock();
        return Promise.resolve();
    };
    CoreStitchAuth.prototype.close = function () {
        if (this.accessTokenRefresher) {
            this.accessTokenRefresher.stop();
        }
    };
    CoreStitchAuth.prototype.prepareAuthRequest = function (stitchReq, authInfo) {
        if (!authInfo.isLoggedIn) {
            throw new StitchClientError(StitchClientErrorCode.MustAuthenticateFirst);
        }
        var newReq = stitchReq.builder;
        var newHeaders = newReq.headers || {};
        if (stitchReq.useRefreshToken) {
            newHeaders[Headers.AUTHORIZATION] = Headers.getAuthorizationBearer(authInfo.refreshToken);
        }
        else {
            newHeaders[Headers.AUTHORIZATION] = Headers.getAuthorizationBearer(authInfo.accessToken);
        }
        newReq.withHeaders(newHeaders);
        return newReq.build();
    };
    CoreStitchAuth.prototype.handleAuthFailureForEventStream = function (ex, req, open) {
        var _this = this;
        if (open === void 0) { open = true; }
        if (!(ex instanceof StitchServiceError) ||
            ex.errorCode !== StitchServiceErrorCode.InvalidSession) {
            throw ex;
        }
        if (req.useRefreshToken || !req.shouldRefreshOnFailure) {
            this.clearActiveUserAuth();
            throw ex;
        }
        return this.tryRefreshAccessToken(req.startedAt).then(function () {
            return _this.openAuthenticatedEventStream(req.builder.withShouldRefreshOnFailure(false).build(), open);
        });
    };
    CoreStitchAuth.prototype.handleAuthFailure = function (ex, req) {
        var _this = this;
        if (!(ex instanceof StitchServiceError) ||
            ex.errorCode !== StitchServiceErrorCode.InvalidSession) {
            throw ex;
        }
        if (req.useRefreshToken || !req.shouldRefreshOnFailure) {
            this.clearActiveUserAuth();
            throw ex;
        }
        return this.tryRefreshAccessToken(req.startedAt).then(function () {
            return _this.doAuthenticatedRequest(req.builder.withShouldRefreshOnFailure(false).build());
        });
    };
    CoreStitchAuth.prototype.tryRefreshAccessToken = function (reqStartedAt) {
        if (!this.isLoggedIn) {
            throw new StitchClientError(StitchClientErrorCode.LoggedOutDuringRequest);
        }
        try {
            var jwt = JWT.fromEncoded(this.activeUserAuthInfo.accessToken);
            if (jwt.issuedAt >= reqStartedAt) {
                return Promise.resolve();
            }
        }
        catch (e) {
        }
        return this.refreshAccessToken();
    };
    CoreStitchAuth.prototype.prepUser = function (authInfo) {
        return this.userFactory.makeUser(authInfo.userId, authInfo.loggedInProviderType, authInfo.loggedInProviderName, authInfo.isLoggedIn, authInfo.lastAuthActivity, authInfo.userProfile);
    };
    CoreStitchAuth.prototype.attachAuthOptions = function (authBody) {
        var options = {};
        options[DEVICE] = this.deviceInfo;
        authBody[OPTIONS] = options;
    };
    CoreStitchAuth.prototype.doLogin = function (credential, asLinkRequest) {
        var _this = this;
        var previousActiveUser = this.currentUser;
        return this.doLoginRequest(credential, asLinkRequest)
            .then(function (response) { return _this.processLoginResponse(credential, response, asLinkRequest); })
            .then(function (user) {
            _this.onAuthEvent();
            if (asLinkRequest) {
                _this.dispatchAuthEvent({
                    kind: AuthEventKind.UserLinked,
                    linkedUser: user
                });
            }
            else {
                _this.dispatchAuthEvent({
                    kind: AuthEventKind.UserLoggedIn,
                    loggedInUser: user,
                });
                _this.dispatchAuthEvent({
                    currentActiveUser: user,
                    kind: AuthEventKind.ActiveUserChanged,
                    previousActiveUser: previousActiveUser
                });
            }
            return user;
        });
    };
    CoreStitchAuth.prototype.doLoginRequest = function (credential, asLinkRequest) {
        var reqBuilder = new StitchDocRequest.Builder();
        reqBuilder.withMethod(Method.POST);
        if (asLinkRequest) {
            reqBuilder.withPath(this.authRoutes.getAuthProviderLinkRoute(credential.providerName));
        }
        else {
            reqBuilder.withPath(this.authRoutes.getAuthProviderLoginRoute(credential.providerName));
        }
        var material = credential.material;
        this.attachAuthOptions(material);
        reqBuilder.withDocument(material);
        if (!asLinkRequest) {
            return this.requestClient.doRequest(reqBuilder.build());
        }
        var linkRequest = new StitchAuthDocRequest(reqBuilder.build(), reqBuilder.document);
        return this.doAuthenticatedRequest(linkRequest);
    };
    CoreStitchAuth.prototype.processLogin = function (credential, newAuthInfo, asLinkRequest) {
        var _this = this;
        var oldActiveUserInfo = this.activeUserAuthInfo;
        var oldActiveUser = this.currentUser;
        newAuthInfo = this.activeUserAuthInfo.merge(new AuthInfo(newAuthInfo.userId, newAuthInfo.deviceId, newAuthInfo.accessToken, newAuthInfo.refreshToken, credential.providerType, credential.providerName, undefined, undefined));
        this.activeUserAuthInfo = newAuthInfo;
        this.currentUser = this.userFactory.makeUser(this.activeUserAuthInfo.userId, credential.providerType, credential.providerName, this.activeUserAuthInfo.isLoggedIn, new Date(), undefined, JWT.fromEncoded(newAuthInfo.accessToken).userData);
        return this.doGetUserProfile()
            .then(function (profile) {
            if (oldActiveUserInfo.hasUser) {
                _this.allUsersAuthInfo.set(oldActiveUserInfo.userId, oldActiveUserInfo.withNewAuthActivityTime());
            }
            newAuthInfo = newAuthInfo.merge(new AuthInfo(newAuthInfo.userId, newAuthInfo.deviceId, newAuthInfo.accessToken, newAuthInfo.refreshToken, credential.providerType, credential.providerName, new Date(), profile));
            var newUserAdded = !_this.allUsersAuthInfo.has(newAuthInfo.userId);
            try {
                writeActiveUserAuthInfoToStorage(newAuthInfo, _this.storage);
                _this.allUsersAuthInfo.set(newAuthInfo.userId, newAuthInfo);
                writeAllUsersAuthInfoToStorage(_this.allUsersAuthInfo, _this.storage);
            }
            catch (err) {
                _this.activeUserAuthInfo = oldActiveUserInfo;
                _this.currentUser = oldActiveUser;
                if (newAuthInfo.userId !== oldActiveUserInfo.userId && newAuthInfo.userId) {
                    _this.allUsersAuthInfo.delete(newAuthInfo.userId);
                }
                throw new StitchClientError(StitchClientErrorCode.CouldNotPersistAuthInfo);
            }
            _this.activeUserAuthInfo = newAuthInfo;
            _this.currentUser = _this.userFactory.makeUser(_this.activeUserAuthInfo.userId, credential.providerType, credential.providerName, _this.activeUserAuthInfo.isLoggedIn, _this.activeUserAuthInfo.lastAuthActivity, profile, JWT.fromEncoded(newAuthInfo.accessToken).userData);
            if (newUserAdded) {
                _this.onAuthEvent();
                _this.dispatchAuthEvent({
                    addedUser: _this.currentUser,
                    kind: AuthEventKind.UserAdded
                });
            }
            return _this.currentUser;
        })
            .catch(function (err) {
            if (err instanceof StitchClientError) {
                throw err;
            }
            if (asLinkRequest || oldActiveUserInfo.hasUser) {
                var linkedAuthInfo = _this.activeUserAuthInfo;
                _this.activeUserAuthInfo = oldActiveUserInfo;
                _this.currentUser = oldActiveUser;
                if (asLinkRequest) {
                    _this.activeUserAuthInfo = _this.activeUserAuthInfo.withAuthProvider(linkedAuthInfo.loggedInProviderType, linkedAuthInfo.loggedInProviderName);
                }
            }
            else {
                _this.clearActiveUserAuth();
            }
            throw err;
        });
    };
    CoreStitchAuth.prototype.processLoginResponse = function (credential, response, asLinkRequest) {
        try {
            if (!response) {
                throw new StitchServiceError("the login response could not be processed for credential: " + credential + ";" +
                    "response was undefined");
            }
            if (!response.body) {
                throw new StitchServiceError("response with status code " + response.statusCode + " has empty body");
            }
            return this.processLogin(credential, ApiAuthInfo.fromJSON(JSON.parse(response.body)), asLinkRequest);
        }
        catch (err) {
            throw new StitchRequestError(err, StitchRequestErrorCode.DECODING_ERROR);
        }
    };
    CoreStitchAuth.prototype.doGetUserProfile = function () {
        var reqBuilder = new StitchAuthRequest.Builder();
        reqBuilder.withMethod(Method.GET).withPath(this.authRoutes.profileRoute);
        return this.doAuthenticatedRequest(reqBuilder.build())
            .then(function (response) { return ApiCoreUserProfile.fromJSON(JSON.parse(response.body)); })
            .catch(function (err) {
            if (err instanceof StitchError) {
                throw err;
            }
            else {
                throw new StitchRequestError(err, StitchRequestErrorCode.DECODING_ERROR);
            }
        });
    };
    CoreStitchAuth.prototype.doLogout = function (authInfo) {
        var reqBuilder = new StitchAuthRequest.Builder();
        reqBuilder
            .withRefreshToken()
            .withPath(this.authRoutes.sessionRoute)
            .withMethod(Method.DELETE);
        return this.doAuthenticatedRequest(reqBuilder.build(), authInfo).then(function () {
            return;
        });
    };
    CoreStitchAuth.prototype.clearActiveUserAuth = function () {
        if (!this.isLoggedIn) {
            return;
        }
        this.clearUserAuthTokens(this.activeUserAuthInfo.userId);
    };
    CoreStitchAuth.prototype.clearUserAuthTokens = function (userId) {
        var unclearedAuthInfo = this.allUsersAuthInfo.get(userId);
        if (unclearedAuthInfo === undefined) {
            if (this.activeUserAuthInfo.userId !== userId) {
                throw new StitchClientError(StitchClientErrorCode.UserNotFound);
            }
        }
        else if (!unclearedAuthInfo.isLoggedIn) {
            return;
        }
        try {
            var loggedOutUser = void 0;
            if (unclearedAuthInfo) {
                var loggedOutAuthInfo = unclearedAuthInfo.loggedOut();
                this.allUsersAuthInfo.set(userId, loggedOutAuthInfo);
                writeAllUsersAuthInfoToStorage(this.allUsersAuthInfo, this.storage);
                loggedOutUser = this.userFactory.makeUser(loggedOutAuthInfo.userId, loggedOutAuthInfo.loggedInProviderType, loggedOutAuthInfo.loggedInProviderName, loggedOutAuthInfo.isLoggedIn, loggedOutAuthInfo.lastAuthActivity, loggedOutAuthInfo.userProfile);
            }
            var wasActiveUser = false;
            if (this.activeUserAuthInfo.hasUser && this.activeUserAuthInfo.userId === userId) {
                wasActiveUser = true;
                this.activeUserAuthInfo = this.activeUserAuthInfo.withClearedUser();
                this.currentUser = undefined;
                writeActiveUserAuthInfoToStorage(this.activeUserAuthInfo, this.storage);
            }
            if (loggedOutUser) {
                this.onAuthEvent();
                this.dispatchAuthEvent({
                    kind: AuthEventKind.UserLoggedOut,
                    loggedOutUser: loggedOutUser,
                });
                if (wasActiveUser) {
                    this.dispatchAuthEvent({
                        currentActiveUser: undefined,
                        kind: AuthEventKind.ActiveUserChanged,
                        previousActiveUser: loggedOutUser
                    });
                }
            }
        }
        catch (err) {
            throw new StitchClientError(StitchClientErrorCode.CouldNotPersistAuthInfo);
        }
    };
    return CoreStitchAuth;
}());
export default CoreStitchAuth;
//# sourceMappingURL=CoreStitchAuth.js.map